import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  ABOUT_US_DEFAULTS,
  ABOUT_US_EMPTY_STATE,
} from "../../../data/aboutUsDefaults";

const ENV_API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

const API = `${ENV_API_BASE_URL}/api/about-us`;

const clone = (value) => JSON.parse(JSON.stringify(value));

const resolveUploadUrl = (value) => {
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  if (value.startsWith("/uploads/")) return `${ENV_API_BASE_URL}${value}`;
  if (value.startsWith("uploads/")) return `${ENV_API_BASE_URL}/${value}`;
  return `${ENV_API_BASE_URL}/uploads/${value}`;
};

const normalizePageData = (item) => {
  const defaults = ABOUT_US_EMPTY_STATE();
  const data = item || {};

  return {
    hero: {
      ...defaults.hero,
      ...(data.hero || {}),
      preview: resolveUploadUrl(data.hero?.heroImage),
      heroImage: data.hero?.heroImage || "",
    },
    stats:
      data.stats?.length > 0
        ? data.stats.map((stat, index) => ({
            ...clone(ABOUT_US_DEFAULTS.stats[index] || ABOUT_US_DEFAULTS.stats[0]),
            ...stat,
          }))
        : defaults.stats,
    floatingCards:
      data.floatingCards?.length > 0
        ? data.floatingCards.map((card, index) => ({
            ...clone(
              ABOUT_US_DEFAULTS.floatingCards[index] ||
                ABOUT_US_DEFAULTS.floatingCards[0]
            ),
            ...card,
          }))
        : defaults.floatingCards,
    overview: {
      ...defaults.overview,
      ...(data.overview || {}),
      paragraphs:
        data.overview?.paragraphs?.length > 0
          ? data.overview.paragraphs
          : defaults.overview.paragraphs,
      highlights:
        data.overview?.highlights?.length > 0
          ? data.overview.highlights.map((highlight, index) => ({
              ...clone(
                ABOUT_US_DEFAULTS.overview.highlights[index] ||
                  ABOUT_US_DEFAULTS.overview.highlights[0]
              ),
              ...highlight,
            }))
          : defaults.overview.highlights,
      preview: resolveUploadUrl(data.overview?.aboutImage),
      aboutImage: data.overview?.aboutImage || "",
    },
    missionVision: {
      ...defaults.missionVision,
      ...(data.missionVision || {}),
      missionPoints:
        data.missionVision?.missionPoints?.length > 0
          ? data.missionVision.missionPoints
          : defaults.missionVision.missionPoints,
      visionPoints:
        data.missionVision?.visionPoints?.length > 0
          ? data.missionVision.visionPoints
          : defaults.missionVision.visionPoints,
    },
    values: {
      ...defaults.values,
      ...(data.values || {}),
      items:
        data.values?.items?.length > 0
          ? data.values.items.map((itemValue, index) => ({
              ...clone(
                ABOUT_US_DEFAULTS.values.items[index] ||
                  ABOUT_US_DEFAULTS.values.items[0]
              ),
              ...itemValue,
            }))
          : defaults.values.items,
    },
    timeline: {
      ...defaults.timeline,
      ...(data.timeline || {}),
      items:
        data.timeline?.items?.length > 0
          ? data.timeline.items
          : defaults.timeline.items,
    },
    whyChoose: {
      ...defaults.whyChoose,
      ...(data.whyChoose || {}),
      items:
        data.whyChoose?.items?.length > 0
          ? data.whyChoose.items
          : defaults.whyChoose.items,
    },
    goals: {
      ...defaults.goals,
      ...(data.goals || {}),
      items:
        data.goals?.items?.length > 0 ? data.goals.items : defaults.goals.items,
    },
    facilities: {
      ...defaults.facilities,
      ...(data.facilities || {}),
      items:
        data.facilities?.items?.length > 0
          ? data.facilities.items
          : defaults.facilities.items,
    },
    studentReviews: {
      ...defaults.studentReviews,
      ...(data.studentReviews || {}),
    },
    cta: {
      ...defaults.cta,
      ...(data.cta || {}),
    },
  };
};

function AboutUsForm() {
  const [recordId, setRecordId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [formState, setFormState] = useState(() => normalizePageData(null));

  const showToast = useCallback((msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3200);
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(API);
      const latest = Array.isArray(res.data?.data)
        ? res.data.data[0]
        : Array.isArray(res.data)
        ? res.data[0]
        : res.data?.data || res.data || null;

      setRecordId(latest?._id || null);
      setFormState(normalizePageData(latest));
    } catch (error) {
      console.error("Failed to load About Us page data:", error);
      setRecordId(null);
      setFormState(normalizePageData(null));
      showToast("Failed to load existing About Us page data", "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const itemCounts = useMemo(
    () => ({
      stats: formState.stats.length,
      cards: formState.floatingCards.length,
      values: formState.values.items.length,
      timeline: formState.timeline.items.length,
      whyChoose: formState.whyChoose.items.length,
      goals: formState.goals.items.length,
      facilities: formState.facilities.items.length,
    }),
    [formState]
  );

  const updateHero = (field, value) => {
    setFormState((prev) => ({
      ...prev,
      hero: { ...prev.hero, [field]: value },
    }));
  };

  const updateHeroImage = (file) => {
    if (!file) return;
    setFormState((prev) => ({
      ...prev,
      hero: {
        ...prev.hero,
        heroImage: file,
        preview: URL.createObjectURL(file),
      },
    }));
  };

  const updateOverview = (field, value) => {
    setFormState((prev) => ({
      ...prev,
      overview: { ...prev.overview, [field]: value },
    }));
  };

  const updateOverviewImage = (file) => {
    if (!file) return;
    setFormState((prev) => ({
      ...prev,
      overview: {
        ...prev.overview,
        aboutImage: file,
        preview: URL.createObjectURL(file),
      },
    }));
  };

  const updateListItem = (sectionKey, index, field, value) => {
    setFormState((prev) => {
      const updatedList = [...prev[sectionKey]];
      updatedList[index] = { ...updatedList[index], [field]: value };
      return { ...prev, [sectionKey]: updatedList };
    });
  };

  const addListItem = (sectionKey, factory) => {
    setFormState((prev) => ({
      ...prev,
      [sectionKey]: [...prev[sectionKey], factory()],
    }));
  };

  const removeListItem = (sectionKey, index, fallbackFactory) => {
    setFormState((prev) => {
      const nextList = prev[sectionKey].filter((_, itemIndex) => itemIndex !== index);
      return {
        ...prev,
        [sectionKey]: nextList.length ? nextList : [fallbackFactory()],
      };
    });
  };

  const updateNestedListItem = (groupKey, listKey, index, field, value) => {
    setFormState((prev) => {
      const updatedList = [...prev[groupKey][listKey]];
      updatedList[index] = { ...updatedList[index], [field]: value };
      return {
        ...prev,
        [groupKey]: {
          ...prev[groupKey],
          [listKey]: updatedList,
        },
      };
    });
  };

  const addNestedListItem = (groupKey, listKey, factory) => {
    setFormState((prev) => ({
      ...prev,
      [groupKey]: {
        ...prev[groupKey],
        [listKey]: [...prev[groupKey][listKey], factory()],
      },
    }));
  };

  const removeNestedListItem = (groupKey, listKey, index, fallbackFactory) => {
    setFormState((prev) => {
      const nextList = prev[groupKey][listKey].filter(
        (_, itemIndex) => itemIndex !== index
      );

      return {
        ...prev,
        [groupKey]: {
          ...prev[groupKey],
          [listKey]: nextList.length ? nextList : [fallbackFactory()],
        },
      };
    });
  };

  const updateStringArray = (groupKey, listKey, index, value) => {
    setFormState((prev) => {
      const updated = [...prev[groupKey][listKey]];
      updated[index] = value;
      return {
        ...prev,
        [groupKey]: {
          ...prev[groupKey],
          [listKey]: updated,
        },
      };
    });
  };

  const addStringArray = (groupKey, listKey) => {
    setFormState((prev) => ({
      ...prev,
      [groupKey]: {
        ...prev[groupKey],
        [listKey]: [...prev[groupKey][listKey], ""],
      },
    }));
  };

  const removeStringArray = (groupKey, listKey, index) => {
    setFormState((prev) => {
      const nextList = prev[groupKey][listKey].filter(
        (_, itemIndex) => itemIndex !== index
      );
      return {
        ...prev,
        [groupKey]: {
          ...prev[groupKey],
          [listKey]: nextList.length ? nextList : [""],
        },
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const payload = new FormData();
    payload.append(
      "data",
      JSON.stringify({
        hero: {
          quoteText: formState.hero.quoteText,
          title: formState.hero.title,
          heroImage:
            typeof formState.hero.heroImage === "string"
              ? formState.hero.heroImage
              : "",
        },
        stats: formState.stats,
        floatingCards: formState.floatingCards,
        overview: {
          sectionLabel: formState.overview.sectionLabel,
          sectionTitle: formState.overview.sectionTitle,
          leadText: formState.overview.leadText,
          paragraphs: formState.overview.paragraphs.filter(Boolean),
          aboutImage:
            typeof formState.overview.aboutImage === "string"
              ? formState.overview.aboutImage
              : "",
          mantraSymbol: formState.overview.mantraSymbol,
          mantraText: formState.overview.mantraText,
          highlights: formState.overview.highlights,
        },
        missionVision: {
          ...formState.missionVision,
          missionPoints: formState.missionVision.missionPoints.filter(Boolean),
          visionPoints: formState.missionVision.visionPoints.filter(Boolean),
        },
        values: formState.values,
        timeline: {
          ...formState.timeline,
          items: formState.timeline.items.map((item) => ({
            ...item,
            year: item.year ? Number(item.year) : null,
          })),
        },
        whyChoose: formState.whyChoose,
        goals: formState.goals,
        facilities: formState.facilities,
        studentReviews: formState.studentReviews,
        cta: formState.cta,
      })
    );

    if (formState.hero.heroImage instanceof File) {
      payload.append("heroImage", formState.hero.heroImage);
    }

    if (formState.overview.aboutImage instanceof File) {
      payload.append("aboutImage", formState.overview.aboutImage);
    }

    try {
      if (recordId) {
        try {
          await axios.put(`${API}/${recordId}`, payload, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (error) {
          if (error?.response?.status !== 404) {
            throw error;
          }

          await axios.post(`${API}/create`, payload, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        }
      } else {
        await axios.post(`${API}/create`, payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      showToast("About Us page saved successfully");
      loadData();
    } catch (error) {
      console.error("Failed to save About Us page:", error);
      showToast(
        error?.response?.data?.message || "Failed to save About Us page",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="container pt-5 mt-5">Loading About Us page data...</div>;
  }

  return (
    <div className="container pt-5 mt-5 pb-5">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="mb-1">About Us Page Admin</h3>
          <p className="text-muted mb-0">
            This page uses one shared record across the website. Teachers are
            pulled separately from the Teacher admin page.
          </p>
        </div>
        <span className="badge text-bg-light border px-3 py-2">
          {recordId ? "Editing Live Record" : "New Shared Record"}
        </span>
      </div>

      <div className="alert alert-info">
        <strong>Our Expert Teachers</strong> on the public About Us page is now
        powered by the data from <code>/admin/about-us/Teacher</code>. The
        sections <strong>What Our Students Say</strong> and{" "}
        <strong>Begin Your Transformation Today</strong> remain static for now.
      </div>

      <div className="d-flex flex-wrap gap-2 mb-4">
        <span className="badge text-bg-light border">Stats: {itemCounts.stats}</span>
        <span className="badge text-bg-light border">
          Floating Cards: {itemCounts.cards}
        </span>
        <span className="badge text-bg-light border">
          Values: {itemCounts.values}
        </span>
        <span className="badge text-bg-light border">
          Timeline: {itemCounts.timeline}
        </span>
        <span className="badge text-bg-light border">
          Why Choose: {itemCounts.whyChoose}
        </span>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="card mb-4">
          <div className="card-body">
            <h5>Hero</h5>
            <input
              type="text"
              className="form-control mb-2"
              placeholder="Quote Text"
              value={formState.hero.quoteText}
              onChange={(e) => updateHero("quoteText", e.target.value)}
            />
            <input
              type="text"
              className="form-control mb-2"
              placeholder="Hero Title"
              value={formState.hero.title}
              onChange={(e) => updateHero("title", e.target.value)}
            />
            <input
              type="file"
              className="form-control mb-2"
              onChange={(e) => updateHeroImage(e.target.files?.[0])}
            />
            {formState.hero.preview && (
              <img src={formState.hero.preview} width="150" alt="Hero preview" />
            )}
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">Hero Stats</h5>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={() =>
                  addListItem("stats", () => ({
                    icon: "",
                    value: "",
                    label: "",
                  }))
                }
              >
                + Add Stat
              </button>
            </div>

            {formState.stats.map((stat, index) => (
              <div className="row border rounded p-3 mb-3" key={`stat-${index}`}>
                <div className="col-md-2 mb-2">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Icon"
                    value={stat.icon}
                    onChange={(e) =>
                      updateListItem("stats", index, "icon", e.target.value)
                    }
                  />
                </div>
                <div className="col-md-4 mb-2">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Value"
                    value={stat.value}
                    onChange={(e) =>
                      updateListItem("stats", index, "value", e.target.value)
                    }
                  />
                </div>
                <div className="col-md-4 mb-2">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Label"
                    value={stat.label}
                    onChange={(e) =>
                      updateListItem("stats", index, "label", e.target.value)
                    }
                  />
                </div>
                <div className="col-md-2 mb-2 d-flex align-items-start">
                  <button
                    type="button"
                    className="btn btn-outline-danger w-100"
                    onClick={() =>
                      removeListItem("stats", index, () => ({
                        icon: "",
                        value: "",
                        label: "",
                      }))
                    }
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">Floating Cards</h5>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={() =>
                  addListItem("floatingCards", () => ({
                    icon: "",
                    title: "",
                    description: "",
                  }))
                }
              >
                + Add Card
              </button>
            </div>

            {formState.floatingCards.map((card, index) => (
              <div className="border rounded p-3 mb-3" key={`card-${index}`}>
                <input
                  type="text"
                  className="form-control mb-2"
                  placeholder="Icon"
                  value={card.icon}
                  onChange={(e) =>
                    updateListItem("floatingCards", index, "icon", e.target.value)
                  }
                />
                <input
                  type="text"
                  className="form-control mb-2"
                  placeholder="Title"
                  value={card.title}
                  onChange={(e) =>
                    updateListItem("floatingCards", index, "title", e.target.value)
                  }
                />
                <textarea
                  className="form-control mb-2"
                  rows="3"
                  placeholder="Description"
                  value={card.description}
                  onChange={(e) =>
                    updateListItem(
                      "floatingCards",
                      index,
                      "description",
                      e.target.value
                    )
                  }
                />
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={() =>
                    removeListItem("floatingCards", index, () => ({
                      icon: "",
                      title: "",
                      description: "",
                    }))
                  }
                >
                  Remove Card
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-body">
            <h5>Overview Section</h5>
            <input
              type="text"
              className="form-control mb-2"
              placeholder="Section Label"
              value={formState.overview.sectionLabel}
              onChange={(e) => updateOverview("sectionLabel", e.target.value)}
            />
            <input
              type="text"
              className="form-control mb-2"
              placeholder="Section Title"
              value={formState.overview.sectionTitle}
              onChange={(e) => updateOverview("sectionTitle", e.target.value)}
            />
            <textarea
              className="form-control mb-2"
              rows="4"
              placeholder="Lead Text"
              value={formState.overview.leadText}
              onChange={(e) => updateOverview("leadText", e.target.value)}
            />

            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <strong>Paragraphs</strong>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => addStringArray("overview", "paragraphs")}
                >
                  + Add Paragraph
                </button>
              </div>

              {formState.overview.paragraphs.map((paragraph, index) => (
                <div className="d-flex gap-2 mb-2" key={`paragraph-${index}`}>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={paragraph}
                    onChange={(e) =>
                      updateStringArray(
                        "overview",
                        "paragraphs",
                        index,
                        e.target.value
                      )
                    }
                  />
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={() => removeStringArray("overview", "paragraphs", index)}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>

            <input
              type="file"
              className="form-control mb-2"
              onChange={(e) => updateOverviewImage(e.target.files?.[0])}
            />
            {formState.overview.preview && (
              <img
                src={formState.overview.preview}
                width="150"
                alt="Overview preview"
                className="mb-3"
              />
            )}

            <div className="row">
              <div className="col-md-3 mb-2">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Mantra Symbol"
                  value={formState.overview.mantraSymbol}
                  onChange={(e) => updateOverview("mantraSymbol", e.target.value)}
                />
              </div>
              <div className="col-md-9 mb-2">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Mantra Text"
                  value={formState.overview.mantraText}
                  onChange={(e) => updateOverview("mantraText", e.target.value)}
                />
              </div>
            </div>

            <div className="d-flex justify-content-between align-items-center mt-3 mb-2">
              <strong>Highlights</strong>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={() =>
                  addNestedListItem("overview", "highlights", () => ({
                    icon: "",
                    title: "",
                    description: "",
                  }))
                }
              >
                + Add Highlight
              </button>
            </div>

            {formState.overview.highlights.map((item, index) => (
              <div className="border rounded p-3 mb-3" key={`highlight-${index}`}>
                <input
                  type="text"
                  className="form-control mb-2"
                  placeholder="Icon"
                  value={item.icon}
                  onChange={(e) =>
                    updateNestedListItem(
                      "overview",
                      "highlights",
                      index,
                      "icon",
                      e.target.value
                    )
                  }
                />
                <input
                  type="text"
                  className="form-control mb-2"
                  placeholder="Title"
                  value={item.title}
                  onChange={(e) =>
                    updateNestedListItem(
                      "overview",
                      "highlights",
                      index,
                      "title",
                      e.target.value
                    )
                  }
                />
                <textarea
                  className="form-control mb-2"
                  rows="3"
                  placeholder="Description"
                  value={item.description}
                  onChange={(e) =>
                    updateNestedListItem(
                      "overview",
                      "highlights",
                      index,
                      "description",
                      e.target.value
                    )
                  }
                />
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={() =>
                    removeNestedListItem("overview", "highlights", index, () => ({
                      icon: "",
                      title: "",
                      description: "",
                    }))
                  }
                >
                  Remove Highlight
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-body">
            <h5>Mission & Vision</h5>
            <div className="row">
              <div className="col-md-6">
                <input
                  type="text"
                  className="form-control mb-2"
                  placeholder="Mission Title"
                  value={formState.missionVision.missionTitle}
                  onChange={(e) =>
                    setFormState((prev) => ({
                      ...prev,
                      missionVision: {
                        ...prev.missionVision,
                        missionTitle: e.target.value,
                      },
                    }))
                  }
                />
                <textarea
                  className="form-control mb-2"
                  rows="4"
                  placeholder="Mission Description"
                  value={formState.missionVision.missionDescription}
                  onChange={(e) =>
                    setFormState((prev) => ({
                      ...prev,
                      missionVision: {
                        ...prev.missionVision,
                        missionDescription: e.target.value,
                      },
                    }))
                  }
                />
                <strong>Mission Points</strong>
                {formState.missionVision.missionPoints.map((point, index) => (
                  <div className="d-flex gap-2 mt-2" key={`mission-${index}`}>
                    <input
                      type="text"
                      className="form-control"
                      value={point}
                      onChange={(e) =>
                        updateStringArray(
                          "missionVision",
                          "missionPoints",
                          index,
                          e.target.value
                        )
                      }
                    />
                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      onClick={() =>
                        removeStringArray(
                          "missionVision",
                          "missionPoints",
                          index
                        )
                      }
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary mt-2"
                  onClick={() => addStringArray("missionVision", "missionPoints")}
                >
                  + Add Mission Point
                </button>
              </div>

              <div className="col-md-6">
                <input
                  type="text"
                  className="form-control mb-2"
                  placeholder="Vision Title"
                  value={formState.missionVision.visionTitle}
                  onChange={(e) =>
                    setFormState((prev) => ({
                      ...prev,
                      missionVision: {
                        ...prev.missionVision,
                        visionTitle: e.target.value,
                      },
                    }))
                  }
                />
                <textarea
                  className="form-control mb-2"
                  rows="4"
                  placeholder="Vision Description"
                  value={formState.missionVision.visionDescription}
                  onChange={(e) =>
                    setFormState((prev) => ({
                      ...prev,
                      missionVision: {
                        ...prev.missionVision,
                        visionDescription: e.target.value,
                      },
                    }))
                  }
                />
                <strong>Vision Points</strong>
                {formState.missionVision.visionPoints.map((point, index) => (
                  <div className="d-flex gap-2 mt-2" key={`vision-${index}`}>
                    <input
                      type="text"
                      className="form-control"
                      value={point}
                      onChange={(e) =>
                        updateStringArray(
                          "missionVision",
                          "visionPoints",
                          index,
                          e.target.value
                        )
                      }
                    />
                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      onClick={() =>
                        removeStringArray("missionVision", "visionPoints", index)
                      }
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary mt-2"
                  onClick={() => addStringArray("missionVision", "visionPoints")}
                >
                  + Add Vision Point
                </button>
              </div>
            </div>
          </div>
        </div>

        {[
          {
            key: "values",
            title: "Core Values",
            hasDesc: true,
            itemsKey: "items",
            numbered: false,
          },
          {
            key: "timeline",
            title: "Timeline",
            hasDesc: true,
            itemsKey: "items",
            timeline: true,
          },
          {
            key: "whyChoose",
            title: "Why Choose Us",
            hasDesc: false,
            itemsKey: "items",
            numbered: true,
          },
          {
            key: "goals",
            title: "Vision Goals",
            hasDesc: false,
            itemsKey: "items",
            numbered: false,
          },
          {
            key: "facilities",
            title: "Facilities",
            hasDesc: false,
            itemsKey: "items",
            numbered: false,
          },
        ].map((section) => (
          <div className="card mb-4" key={section.key}>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">{section.title}</h5>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => {
                    const factory = section.timeline
                      ? () => ({ year: "", title: "", description: "" })
                      : section.numbered
                      ? () => ({ number: "", title: "", description: "" })
                      : () => ({ icon: "", title: "", description: "" });

                    addNestedListItem(section.key, section.itemsKey, factory);
                  }}
                >
                  + Add Item
                </button>
              </div>

              <input
                type="text"
                className="form-control mb-2"
                placeholder="Section Label"
                value={formState[section.key].sectionLabel}
                onChange={(e) =>
                  setFormState((prev) => ({
                    ...prev,
                    [section.key]: {
                      ...prev[section.key],
                      sectionLabel: e.target.value,
                    },
                  }))
                }
              />
              <input
                type="text"
                className="form-control mb-2"
                placeholder="Section Title"
                value={formState[section.key].sectionTitle}
                onChange={(e) =>
                  setFormState((prev) => ({
                    ...prev,
                    [section.key]: {
                      ...prev[section.key],
                      sectionTitle: e.target.value,
                    },
                  }))
                }
              />
              {section.hasDesc && (
                <textarea
                  className="form-control mb-3"
                  rows="3"
                  placeholder="Section Description"
                  value={formState[section.key].sectionDesc}
                  onChange={(e) =>
                    setFormState((prev) => ({
                      ...prev,
                      [section.key]: {
                        ...prev[section.key],
                        sectionDesc: e.target.value,
                      },
                    }))
                  }
                />
              )}

              {formState[section.key][section.itemsKey].map((item, index) => (
                <div className="border rounded p-3 mb-3" key={`${section.key}-${index}`}>
                  {section.timeline ? (
                    <input
                      type="number"
                      className="form-control mb-2"
                      placeholder="Year"
                      value={item.year ?? ""}
                      onChange={(e) =>
                        updateNestedListItem(
                          section.key,
                          section.itemsKey,
                          index,
                          "year",
                          e.target.value
                        )
                      }
                    />
                  ) : section.numbered ? (
                    <input
                      type="text"
                      className="form-control mb-2"
                      placeholder="Number"
                      value={item.number}
                      onChange={(e) =>
                        updateNestedListItem(
                          section.key,
                          section.itemsKey,
                          index,
                          "number",
                          e.target.value
                        )
                      }
                    />
                  ) : (
                    <input
                      type="text"
                      className="form-control mb-2"
                      placeholder="Icon"
                      value={item.icon}
                      onChange={(e) =>
                        updateNestedListItem(
                          section.key,
                          section.itemsKey,
                          index,
                          "icon",
                          e.target.value
                        )
                      }
                    />
                  )}

                  <input
                    type="text"
                    className="form-control mb-2"
                    placeholder="Title"
                    value={item.title}
                    onChange={(e) =>
                      updateNestedListItem(
                        section.key,
                        section.itemsKey,
                        index,
                        "title",
                        e.target.value
                      )
                    }
                  />
                  <textarea
                    className="form-control mb-2"
                    rows="3"
                    placeholder="Description"
                    value={item.description}
                    onChange={(e) =>
                      updateNestedListItem(
                        section.key,
                        section.itemsKey,
                        index,
                        "description",
                        e.target.value
                      )
                    }
                  />
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    onClick={() => {
                      const factory = section.timeline
                        ? () => ({ year: "", title: "", description: "" })
                        : section.numbered
                        ? () => ({ number: "", title: "", description: "" })
                        : () => ({ icon: "", title: "", description: "" });
                      removeNestedListItem(
                        section.key,
                        section.itemsKey,
                        index,
                        factory
                      );
                    }}
                  >
                    Remove Item
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="card mb-4">
          <div className="card-body">
            <h5>Student Reviews Section</h5>
            <p className="text-muted">
              This section uses the shared Google Reviews widget from the homepage.
              Only the About Us headings and intro text are editable here.
            </p>
            <input
              type="text"
              className="form-control mb-2"
              placeholder="Section Label"
              value={formState.studentReviews.sectionLabel}
              onChange={(e) =>
                setFormState((prev) => ({
                  ...prev,
                  studentReviews: {
                    ...prev.studentReviews,
                    sectionLabel: e.target.value,
                  },
                }))
              }
            />
            <input
              type="text"
              className="form-control mb-2"
              placeholder="Section Title"
              value={formState.studentReviews.sectionTitle}
              onChange={(e) =>
                setFormState((prev) => ({
                  ...prev,
                  studentReviews: {
                    ...prev.studentReviews,
                    sectionTitle: e.target.value,
                  },
                }))
              }
            />
            <textarea
              className="form-control"
              rows="3"
              placeholder="Section Description"
              value={formState.studentReviews.sectionDesc}
              onChange={(e) =>
                setFormState((prev) => ({
                  ...prev,
                  studentReviews: {
                    ...prev.studentReviews,
                    sectionDesc: e.target.value,
                  },
                }))
              }
            />
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-body">
            <h5>CTA Section</h5>
            <input
              type="text"
              className="form-control mb-2"
              placeholder="CTA Title"
              value={formState.cta.title}
              onChange={(e) =>
                setFormState((prev) => ({
                  ...prev,
                  cta: {
                    ...prev.cta,
                    title: e.target.value,
                  },
                }))
              }
            />
            <textarea
              className="form-control mb-2"
              rows="3"
              placeholder="CTA Description"
              value={formState.cta.description}
              onChange={(e) =>
                setFormState((prev) => ({
                  ...prev,
                  cta: {
                    ...prev.cta,
                    description: e.target.value,
                  },
                }))
              }
            />
            <input
              type="text"
              className="form-control mb-2"
              placeholder="Primary Button Text"
              value={formState.cta.primaryButtonText}
              onChange={(e) =>
                setFormState((prev) => ({
                  ...prev,
                  cta: {
                    ...prev.cta,
                    primaryButtonText: e.target.value,
                  },
                }))
              }
            />
            <input
              type="text"
              className="form-control"
              placeholder="Secondary Button Text"
              value={formState.cta.secondaryButtonText}
              onChange={(e) =>
                setFormState((prev) => ({
                  ...prev,
                  cta: {
                    ...prev.cta,
                    secondaryButtonText: e.target.value,
                  },
                }))
              }
            />
          </div>
        </div>

        <div className="d-flex gap-2">
          <button type="submit" className="btn btn-success" disabled={saving}>
            {saving ? "Saving..." : "Save About Us Page"}
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={loadData}
            disabled={saving}
          >
            Reload Saved Data
          </button>
        </div>
      </form>

      {toast && (
        <div
          className={`toast show position-fixed bottom-0 end-0 m-4 align-items-center text-white border-0 ${
            toast.type === "error" ? "bg-danger" : "bg-success"
          }`}
          role="status"
          aria-live="polite"
          aria-atomic="true"
          style={{ zIndex: 1080 }}
        >
          <div className="d-flex">
            <div className="toast-body">{toast.msg}</div>
            <button
              type="button"
              className="btn-close btn-close-white me-2 m-auto"
              onClick={() => setToast(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default AboutUsForm;
